import { useState, FormEvent } from 'react';
import { Box, TextField, Button, Typography, Alert, CircularProgress, Link } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useLogin } from '@/hooks/useLogin';
import { AuthLayout } from '@/components/AuthLayout';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const loginMutation = useLogin();
  const location = useLocation();
  const sessionExpired = (location.state as any)?.reason === 'session_expired';

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    loginMutation.mutate({ email, password });
  };

  return (
    <AuthLayout>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>Welcome</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Sign in to manage your school timetable
      </Typography>

      {sessionExpired && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your session expired. Please sign in again.
        </Alert>
      )}

      {loginMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loginMutation.error?.message || 'Login failed. Please try again.'}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Email</Typography>
        <TextField
          fullWidth size="small" type="email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@school.edu" required autoComplete="email" autoFocus
          sx={{ mb: 2 }}
        />
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Password</Typography>
        <TextField
          fullWidth size="small" type="password" value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••" required autoComplete="current-password"
          sx={{ mb: 1 }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Link component={RouterLink} to="/forgot-password" underline="hover" color="primary" variant="body2">
            Forgot password?
          </Link>
        </Box>
        <Button fullWidth type="submit" variant="contained" size="large" disabled={loginMutation.isPending}>
          {loginMutation.isPending ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
        </Button>
      </form>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
        Don't have an account?{' '}
        <Link component={RouterLink} to="/signup" underline="hover" color="primary">Sign up</Link>
      </Typography>

      {/* Spacer so form doesn't hug the top */}
      <Box sx={{ flex: 1 }} />
    </AuthLayout>
  );
}
